'use client';
import { useEffect, useState } from 'react';
import { type TSize } from '../ui-kit/variants';

type TBreakpoints = {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
};

const SCREEN_MATCH_CONFIG = {
  mobile: 768,
  tablet: 1024,
};

const SIZE_TROTTLE = 100;

const FALLBACK_SIZES: TBreakpoints = {
  isDesktop: true,
  isTablet: false,
  isMobile: false,
};

const getSizes = (): TBreakpoints => {
  if (typeof window !== 'object') return FALLBACK_SIZES;
  const width = window.innerWidth;

  return {
    isMobile: width <= SCREEN_MATCH_CONFIG.mobile,
    isTablet:
      width > SCREEN_MATCH_CONFIG.mobile && width <= SCREEN_MATCH_CONFIG.tablet,
    isDesktop: width > SCREEN_MATCH_CONFIG.tablet,
  };
};

// special for shadcn components
export const getSizeByBreakpoints = (breakpoints: TBreakpoints): TSize => {
  if (breakpoints.isMobile) {
    return 'xs';
  }
  if (breakpoints.isTablet) {
    return 's';
  }
  return 'm';
};

export const useMatchScreen = (): TBreakpoints => {
  const [screenSize, setScreenSize] = useState(getSizes());

  const handleResize = (): void => {
    setScreenSize(getSizes());
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout | null;
    const throttledResize = (): void => {
      if (timeout) return;
      timeout = setTimeout(() => {
        handleResize();
        timeout = null;
      }, SIZE_TROTTLE);
    };

    window.addEventListener('resize', throttledResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', throttledResize);
    };
  }, []);

  return screenSize;
};
